import logger from "../utils/logger";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { supabase } from "../supabaseClient";

// Load dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);

export const adjustTime = (datetime) => {
  return dayjs(datetime).format("YYYY-MM-DD HH:mm:ss");
};

// Helper function to properly convert UTC to Amsterdam timezone
export const convertUtcToAmsterdam = (utcDatetime) => {
  if (!utcDatetime) return null;
  const str = String(utcDatetime).trim();
  // If there is no timezone indicator (no Z or +/-offset), the value is stored
  // as Amsterdam local time in a timestamp-without-timezone column.
  // Parse it directly as Amsterdam time so any browser gets the same result.
  if (!/Z$|[+-]\d{2}:?\d{2}$/.test(str)) {
    return dayjs.tz(str, "Europe/Amsterdam");
  }
  // Explicit UTC/offset — convert to Amsterdam
  return dayjs(utcDatetime).tz("Europe/Amsterdam");
};

// Helper function to check if signup deadline has passed (with proper timezone handling)
export const isSignupDeadlinePassed = (signupUntil) => {
  if (!signupUntil) return false;

  const signupUntilAmsterdam = convertUtcToAmsterdam(signupUntil);
  const currentTimeAmsterdam = dayjs().tz("Europe/Amsterdam");

  return currentTimeAmsterdam.isAfter(signupUntilAmsterdam);
};

// Helper function to check if ticket sales have not started yet (signup_from is in the future)
export const isSignupNotStarted = (signupFrom) => {
  if (!signupFrom) return false;

  const signupFromAmsterdam = convertUtcToAmsterdam(signupFrom);
  const currentTimeAmsterdam = dayjs().tz("Europe/Amsterdam");

  return currentTimeAmsterdam.isBefore(signupFromAmsterdam);
};

// Dynamic function to check if current user is admin
export const checkIsAdmin = async (userId) => {
  if (!userId) return false;

  try {
    // Check if user has admin role in their metadata
    const { data, error } = await supabase.auth.admin.getUserById(userId);

    if (error) {
      // Fallback: use RLS function if admin API not available
      const { data: isAdminData, error: rpcError } = await supabase.rpc('has_admin_permissions');
      return rpcError ? false : isAdminData;
    }

    return data?.user?.user_metadata?.role === 'admin' ||
           data?.user?.raw_user_meta_data?.role === 'admin';
  } catch (error) {
    logger.error('Error checking admin status:', error);
    return false;
  }
};

// Synchronous function that uses the has_admin_permissions RLS function
// Result is cached per session to avoid redundant RPC calls
let _adminCachePromise = null;
export const checkIsAdminSync = async () => {
  if (_adminCachePromise !== null) return _adminCachePromise;
  _adminCachePromise = (async () => {
    try {
      const result = await Promise.race([
        supabase.rpc('has_admin_permissions'),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Admin check timeout')), 8000)),
      ]);
      return result.error ? false : result.data;
    } catch (error) {
      logger.error('Error checking admin status:', error);
      _adminCachePromise = null;
      return false;
    }
  })();
  return _adminCachePromise;
};
export const clearAdminCache = () => { _adminCachePromise = null; };

// Check if user has staff or admin panel access (for AdminLayout)
let _staffAccessCachePromise = null;
export const checkStaffAccess = async () => {
  if (_staffAccessCachePromise !== null) return _staffAccessCachePromise;
  _staffAccessCachePromise = (async () => {
    try {
      let { data: { user } } = await supabase.auth.getUser();
      let meta = user?.user_metadata || {};

      // For potential staff users, always refresh the session to get latest role/permissions
      if (meta.role !== 'admin') {
        try {
          const { data: refreshed } = await supabase.auth.refreshSession();
          if (refreshed?.user) {
            user = refreshed.user;
            meta = user.user_metadata || {};
          }
        } catch { /* ignore */ }
      }

      // Try RPC, fall back to metadata if RPC not available
      let hasAccess = false;
      try {
        const rpcResult = await Promise.race([
          supabase.rpc('has_staff_access'),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Staff check timeout')), 8000)),
        ]);
        hasAccess = rpcResult.error ? false : rpcResult.data;
      } catch {
        hasAccess = meta.role === 'admin' || meta.role === 'staff';
      }

      return {
        hasAccess,
        role: meta.role || (hasAccess ? 'admin' : null),
        permissions: meta.permissions || {},
        staffTitle: meta.staff_title || null,
        isFullAdmin: meta.role === 'admin',
      };
    } catch (error) {
      logger.error('Error checking staff access:', error);
      _staffAccessCachePromise = null;
      return { hasAccess: false, role: null, permissions: {}, staffTitle: null, isFullAdmin: false };
    }
  })();
  return _staffAccessCachePromise;
};
export const clearStaffAccessCache = () => { _staffAccessCachePromise = null; };

// Get all admin user IDs dynamically
export const getAdminUserIds = async () => {
  try {
    const { data, error } = await supabase.rpc('get_admin_users');
    if (error) throw error;
    return data?.map(user => user.id) || [];
  } catch (error) {
    logger.error('Error fetching admin users:', error);
    return [];
  }
};

// For backward compatibility - deprecated, use checkIsAdmin instead
export const adminUserIds = [
  "8561c677-63c0-4abf-8916-798ddd18ec14",
  "ecd42b31-a1de-4fbf-a8f1-35b2998b88db",
  "98146fef-eba8-43e6-b400-c05fb96b526e",
  "ba922432-012a-421d-8d6d-e1eb907d05d1",
  "8e638358-6afc-49d5-9d39-f4aa3cf8c2a0",
  "6606a926-9297-4b32-9c11-ab46578432aa",
  "057919e6-ae5f-405c-b911-6a99a638de1a"
];

const STORAGE_PUBLIC_PATH = "/storage/v1/object/public/";
const STORAGE_RENDER_PATH = "/storage/v1/render/image/public/";

/**
 * Public URL for a file in the "images" bucket.
 *
 * Pass `{ width, quality }` for card thumbnails: Supabase serves a resized,
 * re-compressed render instead of the full flyer (a 148 KB poster becomes a
 * ~33 KB thumbnail). Anything that needs the poster at full size (hero,
 * full-screen viewer) must call this without options.
 */
export const getPublicImageUrl = (filename, options) => {
  if (!filename) {
    return filename;
  }

  // Already an absolute URL (CDN, remote flyer) — never rewrite it.
  if (filename.includes("https://") || filename.includes("http://")) {
    return filename;
  }

  const { data } = supabase.storage.from("images").getPublicUrl(filename);
  const publicUrl = data?.publicUrl;
  const width = Number(options?.width) || 0;
  if (!publicUrl || !width || !publicUrl.includes(STORAGE_PUBLIC_PATH)) {
    return publicUrl;
  }

  const params = new URLSearchParams({ width: String(Math.round(width)) });
  // Supabase's image transform defaults to resize=cover, which uses the
  // source height as the target height — so a 1080x1080 flyer comes back as
  // 480x1080, i.e. cropped horizontally instead of scaled. The card then
  // object-cover crops that already-cropped render again, which is what made
  // listing thumbnails look zoomed into the artwork. contain keeps the
  // aspect ratio; CSS object-cover does the (single) crop.
  params.set("resize", "contain");
  const quality = Number(options?.quality) || 0;
  if (quality) params.set("quality", String(Math.round(quality)));

  return `${publicUrl.replace(STORAGE_PUBLIC_PATH, STORAGE_RENDER_PATH)}?${params.toString()}`;
};

export const defaultIcons = [
  "heroicons:shopping-bag",
  "heroicons:gift",
  "heroicons:ticket",
  "heroicons:cake",
  "heroicons:heart",
  "heroicons:sparkles",
  "heroicons:currency-dollar",
  "heroicons:tag",
  "heroicons:star",
  "heroicons:fire",
  "heroicons:light-bulb",
  "heroicons:puzzle-piece",
  "heroicons:musical-note",
  "heroicons:camera",
  "heroicons:film",
  "heroicons:book-open",
  "heroicons:academic-cap",
  "heroicons:briefcase",
  "heroicons:truck",
  "heroicons:building-storefront",
];

// Pure so it is testable without a browser. In production the real site is the
// apex OR www; any other host is a preview deploy and hands off to the test app.
// www used to fall into that branch, so a visitor on www.22night.com was sent to
// 22night-test.vercel.app after signing up.
export const resolveBaseUrl = (hostname = "", nodeEnv = process.env.NODE_ENV) => {
  if (nodeEnv !== "production") return "http://localhost:3000";
  const host = String(hostname || "").toLowerCase();
  if (host === "22night.com" || host === "www.22night.com") return "https://22night.com";
  return "https://22night-test.vercel.app";
};

export const baseUrl = resolveBaseUrl(
  typeof window !== "undefined" ? window.location.hostname : "",
  process.env.NODE_ENV
);

export const availableGenres = [
  "House",
  "Afro",
  "Classics",
  "Disco",
  "Hard-House",
  "Hits",
  "Latin",
  "Techno",
  "RnB",
  "HipHop",
  "Trance",
  "Urban",
  "Amapiano",
];

export const convertToUTC = (datetime) => {
  // Our datetime columns are timestamp-without-timezone and store Amsterdam
  // local time. Format the input as Amsterdam time so the stored value is
  // always Amsterdam local time regardless of the admin's browser locale.
  return dayjs(datetime).tz("Europe/Amsterdam").format("YYYY-MM-DDTHH:mm:ss");
};

export const validateName = (name) => {
  // Only check that the name doesn't contain numbers
  return !/\d/.test(name);
};

export const validateLastname = (name) => {
  // Allow numbers in last names (e.g., for brand names like "22NIGHT")
  // Just check that it's not empty and has at least some content
  return name && name.trim().length > 0;
};

export const validateEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

export const generateSlug = (name) => {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
};
